import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useLocationScope } from "@/context/LocationContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Loader2, Building2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

export default function MonthlyAccounts() {
  const [contracts, setContracts] = useState(null);
  const { selectedLot, isAll, currentLot } = useLocationScope();

  useEffect(() => {
    setContracts(null);
    const url = isAll ? "/contracts" : `/contracts?lotId=${selectedLot}`;
    api.get(url).then(({ data }) => setContracts(data));
  }, [selectedLot, isAll]);

  if (!contracts) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
      </div>
    );
  }

  const pastDue = contracts.filter((c) => c.status === "PastDue").length;
  const mrr = contracts.reduce((sum, c) => sum + (c.monthlyRate || 0), 0);

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-800">
          <Building2 className="h-6 w-6 text-[#A0148C]" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">
            Monthly Accounts
          </h1>
          <p className="text-slate-400">
            {isAll ? "All locations" : currentLot?.name} — corporate contracts with reserved spot blocks.
          </p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="border-slate-800 bg-slate-900 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Accounts</p>
          <p className="font-data text-3xl font-bold text-slate-100">{contracts.length}</p>
        </Card>
        <Card className="border-slate-800 bg-slate-900 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Monthly Recurring</p>
          <p className="font-data text-3xl font-bold text-emerald-400">${mrr.toFixed(0)}</p>
        </Card>
        <Card className="border-slate-800 bg-slate-900 p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Past Due</p>
          <p className={`font-data text-3xl font-bold ${pastDue > 0 ? "text-red-400" : "text-slate-100"}`}>
            {pastDue}
          </p>
        </Card>
      </div>

      <Card className="border-slate-800 bg-slate-900">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="text-slate-400">Ticket #</TableHead>
              <TableHead className="text-slate-400">Account</TableHead>
              <TableHead className="text-slate-400">Reserved Spots</TableHead>
              <TableHead className="text-slate-400">Monthly Rate</TableHead>
              <TableHead className="text-slate-400">Next Bill</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contracts.map((c) => (
              <TableRow
                key={c.id}
                data-testid={`contract-row-${c.id}`}
                className="border-slate-800 hover:bg-slate-800/50"
              >
                <TableCell data-testid={`contract-ticket-${c.id}`} className="whitespace-nowrap font-data text-sm font-bold text-[#d946ef]">{c.ticketNumber || "—"}</TableCell>
                <TableCell className="py-4 font-semibold text-slate-100">{c.companyName}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(c.spotNumbers || []).map((n) => (
                      <span key={n} className="rounded bg-slate-800 px-2 py-0.5 font-data text-xs text-blue-300">
                        #{n}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="font-data text-slate-200">${c.monthlyRate?.toFixed(2)}</TableCell>
                <TableCell className="text-slate-300">{fmtDate(c.nextBillDate)}</TableCell>
                <TableCell>
                  {c.status === "PastDue" ? (
                    <Badge className="bg-red-600 hover:bg-red-600">Past Due</Badge>
                  ) : c.status === "Cancelled" ? (
                    <Badge variant="secondary" className="bg-slate-700">Cancelled</Badge>
                  ) : (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600">Active</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
